import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Text,
  Box,
  Table,
  Thead,
  Tr,
  Th,
  Accordion,
  Td,
  AccordionItem,
  AccordionButton,
  Tbody,
  AccordionPanel,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { Maybe } from 'types/dashboard-core-gqlTypes.generated';

import { WipConfig } from '../../types';
import { useAccordionTableReducer } from '../useAccordionTableReducer';
import {
  CreditAgreement,
  isProperty,
  isPropertyContainer,
  isPropertyContainerType,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { Property } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/property/Property';
import { PropertyContainer } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/property/PropertyContainer';
import { PropertyTable } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/property/PropertyTable';

type CreditAgreementSectionAccordionRowProps = {
  property:
    | CreditAgreement.DomainEntity.Property
    | CreditAgreement.DomainEntity.PropertyContainer;
  wipConfig?: WipConfig;
  index: number;
  basePropertyKey: string;
  propertyKey: string;
  setOpenedAccordionIndices: React.Dispatch<{
    isExpanded: boolean;
    index: number;
  }>;
  openedAccordionIndices: number[];
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  columnWidth: number;
  // eslint-disable-next-line react/no-unused-prop-types
  showOptionalFields: Partial<
    Record<keyof CreditAgreement.DomainEntity.Property, boolean>
  >;
};
export const CreditAgreementSectionAccordionRow = ({
  property,
  wipConfig,
  index,
  basePropertyKey,
  propertyKey,
  setOpenedAccordionIndices,
  openedAccordionIndices,
  headers,
  columnWidth,
}: CreditAgreementSectionAccordionRowProps) => {
  const { type } = property;

  const DropdownTd = () => {
    if (!isPropertyContainerType(type)) {
      return <Td width="5%" />;
    }
    if (_.includes(openedAccordionIndices, index)) {
      return (
        <Td width="5%">
          <ChevronUpIcon />
        </Td>
      );
    }
    return (
      <Td width="5%">
        <ChevronDownIcon />
      </Td>
    );
  };

  return (
    <AccordionItem>
      {({ isExpanded }) => (
        <>
          {isProperty(property) && (
            <>
              <AccordionButton style={{ padding: 0, cursor: 'default' }}>
                <Table style={{ tableLayout: 'fixed' }} borderWidth="1px">
                  <Tbody>
                    <Property
                      headers={headers}
                      basePropertyKey={basePropertyKey}
                      // @ts-ignore
                      property={property}
                      propertyKey={propertyKey}
                      wipConfig={wipConfig}
                      isOptional={
                        !!(
                          wipConfig &&
                          !getIn(
                            wipConfig.wipShell,
                            `${basePropertyKey}.${propertyKey}`,
                          )
                        )
                      }
                      columnWidth={columnWidth}
                      accordion
                    />
                  </Tbody>
                </Table>
              </AccordionButton>
            </>
          )}
          {isPropertyContainer(property) && (
            <>
              <AccordionButton
                onClick={() =>
                  setOpenedAccordionIndices({
                    isExpanded,
                    index,
                  })
                }
                style={{ padding: 0 }}
              >
                <Table style={{ tableLayout: 'fixed' }} borderWidth="1px">
                  <Tbody>
                    <PropertyContainer
                      headers={headers}
                      basePropertyKey={basePropertyKey}
                      property={property}
                      propertyKey={propertyKey}
                      wipConfig={wipConfig}
                      isOptional={
                        !!(
                          wipConfig &&
                          !getIn(
                            wipConfig.wipShell,
                            `${basePropertyKey}.${propertyKey}`,
                          )
                        )
                      }
                      columnWidth={columnWidth}
                      Dropdown={DropdownTd()}
                      // @ts-ignore
                      setOpenedAccordionIndices={setOpenedAccordionIndices}
                      index={index}
                      isExpanded={isExpanded}
                    />
                  </Tbody>
                </Table>
              </AccordionButton>

              <AccordionPanel paddingRight="0">
                <Box height="20px" />
                <Text fontSize="16px" mb="10px" fontWeight="bold">
                  Properties
                </Text>
                <PropertyTable
                  propertyPairs={
                    // @ts-ignore
                    property.contents.map((contentItem) => [
                      propertyKey,
                      contentItem,
                    ])
                  }
                  basePropertyKey={basePropertyKey}
                  wipConfig={wipConfig}
                  isPropertyContainerContent
                />
              </AccordionPanel>
            </>
          )}
        </>
      )}
    </AccordionItem>
  );
};

type CreditAgreementSectionAccordionTableProps = {
  basePropertyKey: string;
  wipConfig?: WipConfig;
  properties: Record<
    string,
    Maybe<
      | CreditAgreement.DomainEntity.Property
      | CreditAgreement.DomainEntity.PropertyContainer
    >
  >;
};
export const CreditAgreementSectionAccordionTable = ({
  wipConfig,
  basePropertyKey,
  properties,
}: CreditAgreementSectionAccordionTableProps) => {
  const {
    openedAccordionIndices,
    setOpenedAccordionIndices,
  } = useAccordionTableReducer();

  const sanitizedProperties = _.omitBy(properties, _.isNil);

  if (
    !sanitizedProperties ||
    _.isEmpty(sanitizedProperties) ||
    !Object.keys(sanitizedProperties).length ||
    !Object.values(sanitizedProperties).length
  ) {
    return <Text>There are currently no properties in this section.</Text>;
  }

  if (
    !wipConfig?.isMutateAllowed(basePropertyKey) &&
    !_.compact(Object.values(sanitizedProperties)).length
  ) {
    return null;
  }

  const accordionTableHeaders: (keyof CreditAgreement.DomainEntity.Property)[] = [
    'name',
    'value',
    'section',
    'description',
  ];

  const showNote =
    _.filter(sanitizedProperties, (property) => {
      if (!property) {
        return false;
      }
      return !_.isNil(property._note_);
    }).length > 0;
  const showIsWrittenIn =
    _.filter(sanitizedProperties, (property) => {
      if (!property) {
        return false;
      }
      return !_.isNil(property._isWrittenIn_);
    }).length > 0;
  if (showNote) {
    accordionTableHeaders.push('_note_');
  }
  if (showIsWrittenIn) {
    accordionTableHeaders.push('_isWrittenIn_');
  }

  // Even column widths with fixed tableLayout so the headers and the accordion rows are always aligned. Dropdown column is fixed to 5%
  // The headers and rows have to be rendered in different tables because an accordion component cannot correctly wrap around an entire table row
  const columnWidth = wipConfig
    ? 95 / accordionTableHeaders.length + 1
    : 95 / accordionTableHeaders.length;

  return (
    <>
      <Table style={{ tableLayout: 'fixed' }} borderWidth="1px">
        <Thead>
          <Tr>
            {_.map(accordionTableHeaders, (header) => {
              return (
                <Th width={`${columnWidth}%`} key={`th-${header}`}>
                  {commonUtils.toTitleCase(header)}
                </Th>
              );
            })}
            {wipConfig && <Th width={`${columnWidth}%`}>Action</Th>}
            <Th width="5%" />
          </Tr>
        </Thead>
      </Table>
      <Accordion allowMultiple index={openedAccordionIndices} pl="0">
        {_.map(
          _.toPairs(sanitizedProperties),
          ([propertyKey, property], index) => {
            if (!property) {
              return null;
            }
            return (
              <CreditAgreementSectionAccordionRow
                key={`${basePropertyKey}-${index}`}
                property={property}
                wipConfig={wipConfig}
                index={index}
                basePropertyKey={basePropertyKey}
                propertyKey={propertyKey}
                setOpenedAccordionIndices={setOpenedAccordionIndices}
                openedAccordionIndices={openedAccordionIndices}
                headers={accordionTableHeaders}
                columnWidth={columnWidth}
                showOptionalFields={{
                  _note_: showNote,
                  _isWrittenIn_: showIsWrittenIn,
                }}
              />
            );
          },
        )}
      </Accordion>
    </>
  );
};
